import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Account, Transaction, Transfer } from '@/types/treasury';

// =====================================================
// ACCOUNTS
// =====================================================

export function useAccounts(companyId: string) {
  return useQuery({
    queryKey: ['accounts', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false});
      
      if (error) throw error;
      return data as Account[];
    },
    enabled: !!companyId,
    staleTime: 0, // Always refetch on mount
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function useAccount(accountId: string) {
  return useQuery({
    queryKey: ['account', accountId],
    queryFn: async () => {
      const { data, error} = await supabase
        .from('accounts')
        .select('*')
        .eq('id', accountId)
        .is('deleted_at', null)
        .single();
      
      if (error) throw error;
      return data as Account;
    },
    enabled: !!accountId,
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (account: Partial<Account>) => {
      const { data, error } = await supabase
        .from('accounts')
        .insert(account)
        .select()
        .single();
      
      if (error) throw error;
      return data as Account;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Account> & { id: string }) => {
      const { data, error } = await supabase
        .from('accounts')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Account;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('accounts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// =====================================================
// TRANSACTIONS
// =====================================================

export function useTransactions(companyId: string, accountId?: string) {
  return useQuery({
    queryKey: ['transactions', companyId, accountId],
    queryFn: async () => {
      let query = supabase
        .from('transactions')
        .select('*')
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false });
      
      if (accountId) {
        query = query.eq('account_id', accountId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as Transaction[];
    },
    enabled: !!companyId,
    staleTime: 0, // Always refetch on mount
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
}

export function useTransaction(transactionId: string) {
  return useQuery({
    queryKey: ['transaction', transactionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('id', transactionId)
        .is('deleted_at', null)
        .single();
      
      if (error) throw error;
      return data as Transaction;
    },
    enabled: !!transactionId,
  });
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (transaction: Partial<Transaction>) => {
      const { data, error } = await supabase
        .from('transactions')
        .insert(transaction)
        .select()
        .single();
      
      if (error) throw error;
      return data as Transaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Transaction> & { id: string }) => {
      const { data, error } = await supabase
        .from('transactions')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Transaction;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('transactions')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// =====================================================
// TRANSFERS
// =====================================================

export function useTransfers(companyId: string) {
  return useQuery({
    queryKey: ['transfers', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transfers')
        .select('*')
        .eq('company_id', companyId)
        .order('transfer_date', { ascending: false });
      
      if (error) throw error;
      return data as Transfer[];
    },
    enabled: !!companyId,
  });
}

export function useCreateTransfer() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (transfer: Partial<Transfer>) => {
      // Step 1: Create the transfer record
      const { data: transferData, error: transferError } = await supabase
        .from('transfers')
        .insert(transfer)
        .select()
        .single();
      
      if (transferError) throw transferError;
      
      // Step 2: Create debit transaction (expense from source account)
      const { data: debitTransaction, error: debitError } = await supabase
        .from('transactions')
        .insert({
          company_id: transfer.company_id,
          account_id: transfer.from_account_id,
          type: 'expense',
          amount: transfer.amount!,
          transaction_date: transfer.transfer_date,
          transaction_number: `${transferData.transfer_number}-OUT`,
          description: `Transfert vers compte - ${transfer.description}`,
          payment_method: 'transfer',
          category: 'transfer',
          created_by: transfer.created_by,
          currency: 'DZD',
        })
        .select()
        .single();
      
      if (debitError) throw debitError;
      
      // Step 3: Create credit transaction (income to destination account)
      const { data: creditTransaction, error: creditError } = await supabase
        .from('transactions')
        .insert({
          company_id: transfer.company_id,
          account_id: transfer.to_account_id,
          type: 'income',
          amount: transfer.amount!,
          transaction_date: transfer.transfer_date,
          transaction_number: `${transferData.transfer_number}-IN`,
          description: `Transfert depuis compte - ${transfer.description}`,
          payment_method: 'transfer',
          category: 'transfer',
          created_by: transfer.created_by,
          currency: 'DZD',
        })
        .select()
        .single();
      
      if (creditError) throw creditError;
      
      // Step 4: Create fee transaction if applicable
      let feeTransaction = null;
      if (transfer.transfer_fee && transfer.transfer_fee > 0) {
        const { data: feeData, error: feeError } = await supabase
          .from('transactions')
          .insert({
            company_id: transfer.company_id,
            account_id: transfer.from_account_id,
            type: 'expense',
            amount: transfer.transfer_fee,
            transaction_date: transfer.transfer_date,
            transaction_number: `${transferData.transfer_number}-FEE`,
            description: `Frais de transfert - ${transfer.description}`,
            payment_method: 'transfer',
            category: 'bank_fees',
            created_by: transfer.created_by,
            currency: 'DZD',
          })
          .select()
          .single();
        
        if (feeError) throw feeError;
        feeTransaction = feeData;
      }
      
      // Step 5: Update transfer with transaction IDs
      const { data: updatedTransfer, error: updateError } = await supabase
        .from('transfers')
        .update({
          debit_transaction_id: debitTransaction.id,
          credit_transaction_id: creditTransaction.id,
          fee_transaction_id: feeTransaction?.id || null,
        })
        .eq('id', transferData.id)
        .select()
        .single();
      
      if (updateError) throw updateError;
      
      return updatedTransfer as Transfer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transfers'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}

// =====================================================
// HELPER QUERIES
// =====================================================

// Get checks expiring soon (150+ days old, approaching 180-day limit)
export function useExpiringChecks(companyId: string) {
  return useQuery({
    queryKey: ['expiring-checks', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('check_expiring_soon');
      
      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
  });
}

// Get account balance summary by type
export function useAccountSummary(companyId: string) {
  const { data: accounts = [] } = useAccounts(companyId);
  
  return {
    total: accounts.reduce((sum, acc) => sum + acc.current_balance, 0),
    byType: {
      bank: accounts
        .filter((a) => a.type === 'bank')
        .reduce((sum, acc) => sum + acc.current_balance, 0),
      ccp: accounts
        .filter((a) => a.type === 'ccp')
        .reduce((sum, acc) => sum + acc.current_balance, 0),
      cash: accounts
        .filter((a) => a.type === 'cash')
        .reduce((sum, acc) => sum + acc.current_balance, 0),
      mobile: accounts
        .filter((a) => a.type === 'mobile_money')
        .reduce((sum, acc) => sum + acc.current_balance, 0),
    },
    accounts: {
      bank: accounts.filter((a) => a.type === 'bank').length,
      ccp: accounts.filter((a) => a.type === 'ccp').length,
      cash: accounts.filter((a) => a.type === 'cash').length,
      mobile: accounts.filter((a) => a.type === 'mobile_money').length,
    },
  };
}

// Get transaction summary
export function useTransactionSummary(companyId: string, period: 'today' | 'week' | 'month' | 'year' = 'month') {
  const { data: transactions = [] } = useTransactions(companyId);
  
  const now = new Date();
  const startDate = new Date();
  
  switch (period) {
    case 'today':
      startDate.setHours(0, 0, 0, 0);
      break;
    case 'week':
      startDate.setDate(now.getDate() - 7);
      break;
    case 'month':
      startDate.setMonth(now.getMonth() - 1);
      break;
    case 'year':
      startDate.setFullYear(now.getFullYear() - 1);
      break;
  }
  
  // Filter transactions: only include those in period AND exclude transfers
  const periodTransactions = transactions.filter(
    (t) => new Date(t.transaction_date) >= startDate && t.category !== 'transfer'
  );
  
  return {
    income: periodTransactions
      .filter((t) => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0),
    expense: periodTransactions
      .filter((t) => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0),
    count: {
      income: periodTransactions.filter((t) => t.type === 'income').length,
      expense: periodTransactions.filter((t) => t.type === 'expense').length,
    },
  };
}
